interface Link {
  label?: string
  url?: string
  image?: CrystallizeImage
}

export interface HomeData {
  title?: string
  description?: string
  podcastCards: PodcastCard[]
  productCards: Packet[]
  highlightedArticles?: HighlightedComponentType
  advCard?: AdvCard
  exploreCard?: {
    title?: string
    defaultImage?: CrystallizeImage
    links?: Link[]
  }
}

export function useHomepage() {
  const { time } = useServerTiming()
  const version = useCrystallizeVersion()
  return useQuery({
    queryKey: ['homepage', version],
    queryFn: async () => {
      const data = await time('GetHomepage', () =>
        $crystallizeFetch({
          document: 'page',
          path: '/forsiden',
          version,
        }))

      const homeData: HomeData = {
        podcastCards: [],
        productCards: [],
      }

      if (data?.catalogue?.components) {
        for (const component of data.catalogue.components) {
          if (!component.content) {
            continue
          }

          switch (component.id) {
            case 'aktuelt-seksjon-artikler': {
              const chunks = getSingleChunks(component)
              const data = getChunkData(chunks)
              const chunk = chunks[0]
              homeData.highlightedArticles = {
                title: chunk && getSingleLine(chunk),
                articles: data.items.map(toArticle).map(toArticleCard),
              }
              break
            }

            case 'podkaster': {
              const items = getItemRelations(component)
              homeData.podcastCards = items.map(toPodcastCard)
              break
            }

            case 'reklame': {
              const item = getItemRelations(component)[0]
              homeData.advCard = item && toAdvCard(item)
              break
            }

            case 'title': {
              homeData.title = getSingleLine(component)
              break
            }

            case 'description': {
              homeData.description = getRichText(component)
              break
            }

            case 'product-cards': {
              const items = getItemRelations(component)
              homeData.productCards = items.map(toPacket)
              break
            }

            case 'explore-card': {
              homeData.exploreCard ??= {}
              const components = getNestedComponents(component)
              for (const child of components) {
                switch (child.id) {
                  case 'title':
                    homeData.exploreCard.title = getSingleLine(child)
                    break
                  case 'default-image':
                    homeData.exploreCard.defaultImage = getSingleImage(child)
                    break
                  case 'links': {
                    homeData.exploreCard.links ||= []
                    const repeatableChunks = getRepeatableChunks(child)
                    for (const chunks of repeatableChunks) {
                      const link: Link = {}
                      for (const chunk of chunks) {
                        switch (chunk.id) {
                          case 'label':
                            link.label = getSingleLine(chunk)
                            break
                          case 'url':
                            link.url = getSingleLine(chunk)
                            break
                          case 'image':
                            link.image = getSingleImage(chunk)
                            break
                        }
                      }
                      if (link.url) {
                        link.label ??= link.url
                        homeData.exploreCard.links.push(link)
                      }
                    }
                    break
                  }
                }
              }
              break
            }
          }
        }
      }

      return homeData
    },
  })
}
